import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/UI/Icon';
import './PrivacyPolicyPage.css';

const LAST_UPDATED = 'June 29, 2026';

function PrivacyPolicyPage() {
  return (
    <div className="privacy-page">
      <header className="page-header">
        <div>
          <h1>Privacy Policy</h1>
          <p className="page-subtitle">Last updated: {LAST_UPDATED}</p>
        </div>
        <Link to="/settings" className="btn btn-ghost">
          <Icon name="chevronLeft" size={18} />
          Settings
        </Link>
      </header>

      <article className="privacy-content">
        <section>
          <h2>Summary</h2>
          <p>
            TruthBound is a local-first Bible study app. Your highlights, notes, journal entries,
            bookmarks, and reading progress are stored on your device — not on our servers.
            We do not sell your data or require an account to use the app.
          </p>
        </section>

        <section>
          <h2>Information we collect</h2>
          <h3>Stored on your device</h3>
          <p>
            The following data stays in your browser or app using IndexedDB and local storage:
          </p>
          <ul>
            <li>Bible text cache for offline reading</li>
            <li>Highlights, notes, bookmarks, and annotations</li>
            <li>Journal entries and reflections</li>
            <li>Reading plans, themes, and search history</li>
            <li>Display preferences (theme, font size, reading position)</li>
          </ul>
          <p>
            You can export a JSON backup from Settings or clear cached Bible data at any time.
            Uninstalling the app or clearing site data removes this information from your device.
          </p>

          <h3>Hosted analytics</h3>
          <p>
            TruthBound uses Vercel Web Analytics to collect anonymous, aggregated usage metrics
            (such as page views and performance). This does not include your journal text, notes,
            highlights, or other personal study content.
          </p>
        </section>

        <section>
          <h2>Information we do not collect</h2>
          <ul>
            <li>No account registration or email required</li>
            <li>No advertising identifiers</li>
            <li>No sale of personal information</li>
            <li>No upload of your study notes or journal to TruthBound servers</li>
          </ul>
        </section>

        <section>
          <h2>Third-party services</h2>
          <p>TruthBound may connect to the following services:</p>
          <ul>
            <li>
              <strong>Vercel</strong> — hosts the website and provides anonymous analytics
            </li>
            <li>
              <strong>Bible text sources</strong> — public-domain and licensed translations
              downloaded to your device for reading and search
            </li>
            <li>
              <strong>Ko-fi</strong> — optional support link; only used if you choose to visit it
            </li>
          </ul>
        </section>

        <section>
          <h2>Mobile apps</h2>
          <p>
            The TruthBound iOS and Android apps are thin native shells that load the same web app
            at <a href="https://truthbound.app">truthbound.app</a>. Your data storage and privacy
            practices are the same as the website. The apps do not access contacts, photos,
            location, or other device sensors.
          </p>
        </section>

        <section>
          <h2>Children&apos;s privacy</h2>
          <p>
            TruthBound does not knowingly collect personal information from children under 13.
            The app does not require registration and stores study data locally on the user&apos;s device.
          </p>
        </section>

        <section>
          <h2>Your choices</h2>
          <ul>
            <li>Export your data from Settings → Data Management</li>
            <li>Clear cached Bible data from Settings → Storage</li>
            <li>Use the app offline after your first visit</li>
            <li>Block or limit analytics through your browser or device settings</li>
          </ul>
        </section>

        <section>
          <h2>Changes to this policy</h2>
          <p>
            We may update this policy as the app evolves. The &quot;Last updated&quot; date at the top
            of this page will change when we do. Continued use of TruthBound after changes means
            you accept the updated policy.
          </p>
        </section>

        <section>
          <h2>Contact</h2>
          <p>
            Questions about this policy? Visit{' '}
            <a href="https://truthbound.app">truthbound.app</a> or open an issue on our GitHub
            repository.
          </p>
        </section>
      </article>
    </div>
  );
}

export default PrivacyPolicyPage;